'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  pushConsentUpdate,
  readConsentCookie,
  writeConsentCookie,
} from '@/lib/consent-cookie';

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';

export type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const DEFAULT_STATE: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
};

type ConsentContextValue = {
  state: ConsentState;
  hasDecided: boolean;
  ready: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (partial: Partial<Omit<ConsentState, 'necessary'>>) => void;
  reopen: () => void;
  isOpen: boolean;
  closePreferences: () => void;
  openPreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

// Chamada direta do Consent Mode, anterior ao evento cookie_consent_update.
// Descontinuar após a homologação do fluxo pelo GTM, junto com o
// gtag('consent', 'default') de app/_gtm.tsx.
function syncGoogleConsent(state: ConsentState) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  window.gtag('consent', 'update', {
    ad_storage: state.marketing ? 'granted' : 'denied',
    ad_user_data: state.marketing ? 'granted' : 'denied',
    ad_personalization: state.marketing ? 'granted' : 'denied',
    analytics_storage: state.analytics ? 'granted' : 'denied',
  });
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConsentState>(DEFAULT_STATE);
  const [hasDecided, setHasDecided] = useState(false);
  const [ready, setReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // O envio do evento no carregamento (e a migração do localStorage antigo)
    // fica no script do <head>; aqui só restauramos o estado do banner.
    const stored = readConsentCookie();
    if (stored) {
      setState({
        necessary: true,
        analytics: stored.analytics,
        marketing: stored.marketing,
      });
      setHasDecided(true);
    }
    setReady(true);
  }, []);

  const commit = useCallback((next: ConsentState) => {
    setState(next);
    setHasDecided(true);
    writeConsentCookie(next);
    pushConsentUpdate(next);
    syncGoogleConsent(next);
    setIsOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    commit({ necessary: true, analytics: true, marketing: true });
  }, [commit]);

  const rejectAll = useCallback(() => {
    commit({ necessary: true, analytics: false, marketing: false });
  }, [commit]);

  const save = useCallback(
    (partial: Partial<Omit<ConsentState, 'necessary'>>) => {
      commit({
        necessary: true,
        analytics: partial.analytics ?? state.analytics,
        marketing: partial.marketing ?? state.marketing,
      });
    },
    [commit, state.analytics, state.marketing]
  );

  const reopen = useCallback(() => setIsOpen(true), []);
  const openPreferences = useCallback(() => setIsOpen(true), []);
  const closePreferences = useCallback(() => setIsOpen(false), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      hasDecided,
      ready,
      acceptAll,
      rejectAll,
      save,
      reopen,
      isOpen,
      openPreferences,
      closePreferences,
    }),
    [
      state,
      hasDecided,
      ready,
      acceptAll,
      rejectAll,
      save,
      reopen,
      isOpen,
      openPreferences,
      closePreferences,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used inside <ConsentProvider>');
  }
  return ctx;
}
